import React, { createContext, useState, useContext, useEffect } from "react";
import accessService from "../../apis/Profile/AccessServiceApi";

const bundleHierarchy = {
  freemium: {
    modules: ["Company"],
  },
  initiator: {
    modules: ["Product", "Feed", "ERP"],
    includes: ["freemium"],
  },
  ambition: {
    modules: ["Ambition"],
    includes: ["initiator", "freemium"],
  },
  together: {
    modules: ["Get Help"],
    includes: ["freemium"],
  },
  others: {
    modules: [
      "Admin",
      "AR",
      "Business",
      "Client",
      "Education",
      "Map",
      "Report",
      "Share",
      "Simulate",
      "Lab",
    ],
  },
};

export const UserModulesContext = createContext({
  userModules: [],
  setUserModules: () => {},
  isModuleAccessible: () => false,
  isLoading: true,
});

export const UserModulesProvider = ({ children }) => {
  const [userModules, setUserModules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const isModuleAccessible = (moduleName) => {
    // If module is directly in user modules, return true
    if (userModules.includes(moduleName)) return true;

    // Check if module is in any bundle hierarchy
    for (const bundleConfig of Object.values(bundleHierarchy)) {
      // Check if module is in the current bundle's modules
      if (bundleConfig.modules.includes(moduleName)) {
        // If module is in this bundle, but not in user modules, it should be locked
        return false;
      }
    }

    // If module is not in any bundle hierarchy, it's considered accessible
    return true;
  };

  useEffect(() => {
    const fetchUserModules = async () => {
      try {
        setIsLoading(true);
        const modules = await accessService.getUserModules();
        setUserModules(modules);
      } catch (error) {
        console.error("Failed to fetch user modules:", error);
        setUserModules([]);
      } finally {
        setIsLoading(false);
      }
    };

    const checkTokenAndFetchModules = () => {
      const accessToken = localStorage.getItem("access_token");
      if (accessToken) {
        fetchUserModules();
      } else {
        const timer = setTimeout(() => {
          const laterToken = localStorage.getItem("access_token");
          if (laterToken) {
            fetchUserModules();
          } else {
            setIsLoading(false);
          }
        }, 500);

        return () => clearTimeout(timer);
      }
    };

    checkTokenAndFetchModules();
  }, []);

  return (
    <UserModulesContext.Provider
      value={{
        userModules,
        setUserModules,
        isModuleAccessible,
        isLoading,
      }}
    >
      {children}
    </UserModulesContext.Provider>
  );
};

export const useUserModules = () => {
  const context = useContext(UserModulesContext);
  if (!context) {
    throw new Error("useUserModules must be used within a UserModulesProvider");
  }
  return context;
};
